import { USER_CONTENT_ENUM } from '@/core/store/user-content/content-context-constants';

export type ContentMode = 'USER' | 'BUSINESS';
export type ContentTab =
  | typeof USER_CONTENT_ENUM.IMAGE
  | typeof USER_CONTENT_ENUM.VIDEO
  | typeof USER_CONTENT_ENUM.TEXT
  | typeof USER_CONTENT_ENUM.FILE
  | typeof USER_CONTENT_ENUM.LINK
  | typeof USER_CONTENT_ENUM.ASSET
  | typeof USER_CONTENT_ENUM.PORTAL;

export interface UserContentContext {
  mode: ContentMode;
  activeTab: ContentTab;
  lastUsedTabs: {
    USER: ContentTab;
    BUSINESS: ContentTab;
  };
  selectedBusinessId?: string;
  vendorId?: string; // Stored vendorId takes priority over ucManager.vendorId
}

const STORAGE_KEY_PREFIX = 'userContentSpaceContext';

/**
 * Get storage key with user ID
 */
function getStorageKey(userId?: string): string {
  // Return user-specific key or fallback to generic key
  return userId ? `${STORAGE_KEY_PREFIX}_${userId}` : STORAGE_KEY_PREFIX;
}

/**
 * Get default context
 */
export function getDefaultContext(): UserContentContext {
  return {
    mode: 'USER',
    activeTab: USER_CONTENT_ENUM.IMAGE,
    lastUsedTabs: {
      USER: USER_CONTENT_ENUM.IMAGE,
      BUSINESS: USER_CONTENT_ENUM.IMAGE,
    },
  };
}

/**
 * Save context to localStorage
 */
export function saveContext(context: UserContentContext, userId?: string): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const storageKey = getStorageKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(context));
  } catch (error) {
    // Handle quota exceeded or other storage errors gracefully
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.warn('[UserContentContext] localStorage quota exceeded, unable to save context');
    } else {
      console.error('Failed to save UserContentSpace context:', error);
    }
  }
}

/**
 * Load context from localStorage
 */
export function loadContext(userId?: string): UserContentContext {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('[UserContentContext] localStorage not available');
      return getDefaultContext();
    }

    const storageKey = getStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return getDefaultContext();
    }

    const parsed = JSON.parse(stored) as UserContentContext;

    // Validate the loaded context
    if (!isValidContext(parsed)) {
      console.warn('[UserContentContext] Invalid stored context, using defaults');
      return getDefaultContext();
    }

    return parsed;
  } catch (error) {
    console.error('Failed to load UserContentSpace context:', error);
    return getDefaultContext();
  }
}

/**
 * Clear stored context
 */
export function clearContext(userId?: string): void {
  try {
    const storageKey = getStorageKey(userId);
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error('Failed to clear UserContentSpace context:', error);
  }
}

/**
 * Clear all user content contexts (for logout)
 */
export function clearAllUserContentContexts(): void {
  try {
    // Get all keys from localStorage
    const keys = Object.keys(localStorage);

    // Remove all keys that start with our prefix
    keys.forEach((key) => {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Failed to clear all UserContentSpace contexts:', error);
  }
}

/**
 * Validate context structure
 */
function isValidContext(context: any): context is UserContentContext {
  if (!context || typeof context !== 'object') {
    return false;
  }

  const validModes: ContentMode[] = ['USER', 'BUSINESS'];
  const validTabs: ContentTab[] = [
    USER_CONTENT_ENUM.IMAGE,
    USER_CONTENT_ENUM.VIDEO,
    USER_CONTENT_ENUM.TEXT,
    USER_CONTENT_ENUM.FILE,
    USER_CONTENT_ENUM.LINK,
    USER_CONTENT_ENUM.ASSET,
    USER_CONTENT_ENUM.PORTAL,
  ];

  return (
    validModes.includes(context.mode) &&
    validTabs.includes(context.activeTab) &&
    context.lastUsedTabs &&
    validTabs.includes(context.lastUsedTabs.USER) &&
    validTabs.includes(context.lastUsedTabs.BUSINESS) &&
    (context.vendorId === undefined || typeof context.vendorId === 'string')
  );
}

/**
 * Update last used tab for a specific mode
 */
export function updateLastUsedTab(
  context: UserContentContext,
  mode: ContentMode,
  tab: ContentTab,
): UserContentContext {
  return {
    ...context,
    lastUsedTabs: {
      ...context.lastUsedTabs,
      [mode]: tab,
    },
  };
}
